import {
  Box,
  Flex,
  HStack,
  VStack,
  Heading,
  Text,
  Icon,
  Skeleton,
  SkeletonText,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  ButtonGroup,
  Badge,
  SimpleGrid,
} from '@chakra-ui/react'
import { useMemo, useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Bar, Doughnut } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  Title,
} from 'chart.js'
import {
  LuActivity,
  LuChevronDown,
  LuTestTube,
  LuTrendingUp,
  LuChartColumn,
} from 'react-icons/lu'
import axiosClient from '../../api/axios'
import { pathologyDisplayName } from '../Lab/BlotAnalysisWorkspace/utils/displayName'
import { useTranslation } from 'react-i18next'
import { useLabAppAccess } from '../../hooks/useLabAppAccess'
import { LAB_APPS, labAppOfBlotModule, type LabApp } from '../../config/labApps'
import { IMMUNOBLOTS, moduleOfPathologyId, resolvePathologyLabel } from '../Lab/BlotAnalysisWorkspace/ImmunoBlotSelection'

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Tooltip, Legend, Title)

type Period = 'this_month' | 'last_3_months' | 'last_6_months' | 'last_12_months' | 'this_year'
// « tout », ou l'identifiant d'une application du registre du Lab Studio.
type ModuleFilter = 'all' | string

interface ActivitySummary {
  period: Period
  module: ModuleFilter
  from: string
  to: string
  totals: {
    blot: number
    alma: number
    all: number
    blotPositiveLanes: number
    blotNegativeLanes: number
    /** Cas blot par identifiant de test — la granularité qui permet de
     *  répartir une même table entre IRIS, PROTIS et la suivante. */
    blotByPathologyId?: Record<string, number>
    /** Le miroir en BANDELETTES (absent d'un serveur pas encore à jour :
     *  le sélecteur Cas/Bandelettes se cache alors). */
    blotStrips?: number
    blotStripsByPathologyId?: Record<string, number>
  }
  byMonth: Array<{
    month: string
    blot: number
    alma: number
    blotByPathologyId?: Record<string, number>
    blotStrips?: number
    blotStripsByPathologyId?: Record<string, number>
  }>
  byPathology: Array<{ name: string; count: number; strips?: number }>
}

/** Ce que les chiffres comptent — même bascule que l'activité par
 *  établissement côté admin : un cas est une plaque déposée, une bandelette un
 *  échantillon lu, et aucun des deux ne remplace l'autre. */
type Measure = 'cases' | 'strips'

/**
 * Le compte blot d'une application : la somme des tests que son module de
 * catalogue revendique. Les identifiants inconnus reviennent à IRIS, le
 * propriétaire historique de la table. Sans la ventilation (réponse d'un
 * serveur pas encore à jour), tout reste à IRIS plutôt que de disparaître.
 */
const blotCountOf = (
  byPid: Record<string, number> | undefined,
  totalBlot: number,
  blotModule: 'iris' | 'protis',
): number => {
  if (!byPid) return blotModule === 'iris' ? totalBlot : 0
  return Object.entries(byPid)
    .filter(([pid]) => moduleOfPathologyId(pid) === blotModule)
    .reduce((sum, [, n]) => sum + n, 0)
}

/**
 * L'application à laquelle appartient une ligne du classement par pathologie.
 * Le serveur compte par NOM (et par libellé imprimé pour les multitests) ;
 * le catalogue sait à qui chaque nom appartient.
 */
const pathologyApp = (name: string): LabApp => {
  if (name === 'Paludisme (ALMA)') {
    return LAB_APPS.find((a) => a.id === 'parasitology') ?? labAppOfBlotModule('iris')
  }
  const entry = IMMUNOBLOTS.find((b) => b.name === name) ?? resolvePathologyLabel(name)
  return labAppOfBlotModule(entry?.module ?? 'iris')
}

// Clés i18n : ces constantes vivent hors composant, traduites au rendu.
const PERIOD_LABELS: Record<Period, string> = {
  this_month: 'home.activityDashboard.thisMonth',
  last_3_months: 'home.activityDashboard.last3Months',
  last_6_months: 'home.activityDashboard.last6Months',
  last_12_months: 'home.activityDashboard.last12Months',
  this_year: 'home.activityDashboard.thisYear',
}

// Le libellé du filtre : « tous » traduit, sinon le nom de l'application.
const moduleFilterLabel = (f: ModuleFilter, t: (k: string) => string): string =>
  f === 'all'
    ? t('home.activityDashboard.allModules')
    : LAB_APPS.find((a) => a.id === f)?.label ?? f

// Palette teal → cyan → blue → indigo → purple → pink — readable on white
const PALETTE = [
  'rgba(13, 148, 136, 0.9)',   // teal-600
  'rgba(6, 182, 212, 0.9)',    // cyan-500
  'rgba(59, 130, 246, 0.9)',   // blue-500
  'rgba(99, 102, 241, 0.9)',   // indigo-500
  'rgba(168, 85, 247, 0.9)',   // purple-500
  'rgba(236, 72, 153, 0.9)',   // pink-500
  'rgba(245, 158, 11, 0.9)',   // amber-500
  'rgba(16, 185, 129, 0.9)',   // emerald-500
]

// Smooth number animation from 0 up to `target` over `durationMs`,
// using an ease-out cubic curve so it lands gently on the final value.
// Re-runs whenever `target` changes (e.g. when the filter is switched).
function useCountUp(target: number, durationMs: number = 900): number {
  const [value, setValue] = useState(0)
  const rafRef = useRef<number | null>(null)
  const startRef = useRef<number | null>(null)
  const fromRef = useRef<number>(0)

  useEffect(() => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    fromRef.current = value
    startRef.current = null

    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)

    const tick = (ts: number) => {
      if (startRef.current === null) startRef.current = ts
      const elapsed = ts - startRef.current
      const progress = Math.min(1, elapsed / durationMs)
      const eased = easeOutCubic(progress)
      const next = Math.round(fromRef.current + (target - fromRef.current) * eased)
      setValue(next)
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick)
      } else {
        rafRef.current = null
      }
    }
    rafRef.current = requestAnimationFrame(tick)

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, durationMs])

  return value
}

const formatMonth = (yyyyMm: string): string => {
  const [yearStr, monthStr] = yyyyMm.split('-')
  const date = new Date(parseInt(yearStr, 10), parseInt(monthStr, 10) - 1, 1)
  return date.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' })
}

interface ActivityDashboardProps {
  /** Admin only: view another user's activity instead of the logged-in one's
   *  — e.g. PilotsAdmin embeds this same widget per pilot, reusing the exact
   *  home-dashboard component/endpoint rather than a bespoke stats block. */
  userId?: string
  /** Smaller heading for embedding inside an already-titled panel (PilotsAdmin's
   *  per-pilot section has its own header), instead of the full dashboard title. */
  title?: string
}

export default function ActivityDashboard({ userId, title }: ActivityDashboardProps = {}) {
  const { t } = useTranslation()
  const [period, setPeriod] = useState<Period>('last_3_months')
  const [moduleFilter, setModuleFilter] = useState<ModuleFilter>('all')
  const [measure, setMeasure] = useState<Measure>('cases')

  /**
   * Une application coupée dans l'administration disparaît du tableau : pas
   * de compteur ALMA, pas de série ALMA dans l'histogramme, pas d'entrée dans
   * le filtre — sinon l'interrupteur ne coupe que la carte du Lab Studio.
   *
   * SAUF en vue admin (`userId`) : un superviseur qui regarde l'activité d'un
   * pilote veut la voir entière, quels que soient SES propres interrupteurs.
   */
  const { isAppEnabled } = useLabAppAccess()
  // Les applications du registre ouvertes à cet utilisateur. Une entrée
  // ajoutée à LAB_APPS obtient ses compteurs, sa série et son filtre ici
  // sans qu'on retouche ce fichier.
  const visibleApps = LAB_APPS.filter((app) => !!userId || isAppEnabled(app.id))
  // Celles que le filtre courant retient — « tout » ou une seule.
  const shownApps = visibleApps.filter((app) => moduleFilter === 'all' || app.id === moduleFilter)

  // Le serveur ne connaît que ses deux tables (blot, alma) : IRIS et PROTIS
  // se départagent ensuite ici, par la ventilation blotByPathologyId.
  const serverModule = moduleFilter === 'all'
    ? 'all'
    : moduleFilter === 'parasitology' ? 'alma' : 'blot'

  const { data, isLoading, error } = useQuery<ActivitySummary>({
    queryKey: ['activitySummary', period, serverModule, userId],
    queryFn: async () => {
      const params = new URLSearchParams({ period, module: serverModule })
      if (userId) params.append('userId', userId)
      const response = await axiosClient.get(`/lab-analysis/activity-summary?${params.toString()}`)
      return response.data
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })

  // Le sélecteur Cas/Bandelettes ne s'affiche que si le serveur ventile déjà
  // les bandelettes (réponse d'un serveur pas encore à jour : cas seulement).
  const stripsAvailable = data?.totals?.blotStrips !== undefined
  const effectiveMeasure: Measure = stripsAvailable ? measure : 'cases'
  /** La valeur d'une ligne du classement, dans l'unité choisie. ALMA n'a pas
   *  de bandelettes : un test = un échantillon, même unité des deux côtés. */
  const pathoValue = (p: { count: number; strips?: number }): number =>
    (effectiveMeasure === 'strips' ? (p.strips ?? p.count) : p.count)

  /** Le compte d'une application dans la fenêtre chargée. */
  const appCount = (app: LabApp): number => {
    if (!data) return 0
    if (!app.blotModule) return data.totals.alma ?? 0
    if (effectiveMeasure === 'strips') {
      return blotCountOf(data.totals.blotStripsByPathologyId, data.totals.blotStrips ?? 0, app.blotModule)
    }
    return blotCountOf(data.totals.blotByPathologyId, data.totals.blot ?? 0, app.blotModule)
  }

  // Le classement par pathologie, restreint aux applications visibles ET au
  // filtre courant : une pathologie PROTIS n'a rien à faire dans le camembert
  // d'un utilisateur qui n'a pas PROTIS, ni sous le filtre IRIS.
  const visiblePathologies = useMemo(() => {
    const shown = new Set(shownApps.map((a) => a.id))
    return (data?.byPathology ?? [])
      .filter((p) => shown.has(pathologyApp(p.name).id))
      // Reclassé dans l'unité choisie : le serveur trie par cas, et le top en
      // bandelettes n'est pas forcément le même.
      .sort((a, b) => pathoValue(b) - pathoValue(a))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.byPathology, moduleFilter, visibleApps.length, effectiveMeasure])

  const doughnutData = useMemo(() => {
    if (!visiblePathologies.length) return null
    const top = visiblePathologies.slice(0, 8)
    return {
      labels: top.map((p) => pathologyDisplayName(p.name)),
      datasets: [{
        data: top.map((p) => pathoValue(p)),
        backgroundColor: top.map((_, i) => PALETTE[i % PALETTE.length]),
        borderColor: 'white',
        borderWidth: 2,
        hoverOffset: 6,
      }],
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visiblePathologies, effectiveMeasure])

  const barData = useMemo(() => {
    if (!data?.byMonth?.length) return null
    return {
      labels: data.byMonth.map((m) => formatMonth(m.month)),
      datasets: shownApps.map((app) => ({
        label: app.label,
        data: data.byMonth.map((m) => {
          if (!app.blotModule) return m.alma
          return effectiveMeasure === 'strips'
            ? blotCountOf(m.blotStripsByPathologyId, m.blotStrips ?? 0, app.blotModule)
            : blotCountOf(m.blotByPathologyId, m.blot, app.blotModule)
        }),
        backgroundColor: app.accentColor,
        borderRadius: 6,
        borderSkipped: false,
        stack: 'stack0',
      })),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.byMonth, moduleFilter, visibleApps.length, effectiveMeasure])

  // Le total ne compte que ce qui s'affiche : un « total » qui inclurait une
  // application masquée trahirait ses chiffres par simple soustraction.
  const totalsHeader = shownApps.reduce((sum, app) => sum + appCount(app), 0)
  const pathologyTotal = visiblePathologies.reduce((sum, p) => sum + pathoValue(p), 0)
  // Le mot qui va avec les chiffres affichés — partout le même.
  const unitWord = effectiveMeasure === 'strips' ? 'bandelettes' : 'tests'
  const mostUsedPathology = visiblePathologies[0]?.name
    ? pathologyDisplayName(visiblePathologies[0].name)
    : '—'

  // Smooth count-up for the doughnut center total — replays each time the
  // dataset changes (filter switch, refetch, etc.).
  const animatedTotal = useCountUp(totalsHeader, 900)

  return (
    <Box
      data-tour-id={userId ? undefined : 'home-activity'}
      bg="white"
      borderRadius="2xl"
      boxShadow="0 10px 30px -12px rgba(15, 118, 110, 0.12)"
      border="1px solid"
      borderColor="gray.100"
      p={{ base: 5, md: 6 }}
      position="relative"
      overflow="hidden"
    >
      {/* Decorative gradient header strip */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        h="4px"
        bgGradient="linear(to-r, teal.400, cyan.400, blue.400, purple.400)"
      />

      {/* Subtle decorative radial blobs for depth */}
      <Box
        position="absolute"
        top="-80px"
        right="-80px"
        w="240px"
        h="240px"
        borderRadius="full"
        bgGradient="radial(rgba(20,184,166,0.10), transparent 70%)"
        pointerEvents="none"
      />
      <Box
        position="absolute"
        bottom="-60px"
        left="-60px"
        w="180px"
        h="180px"
        borderRadius="full"
        bgGradient="radial(rgba(168,85,247,0.08), transparent 70%)"
        pointerEvents="none"
      />

      {/* Header */}
      <Flex
        justify="space-between"
        align={{ base: 'flex-start', md: 'center' }}
        mb={6}
        flexWrap="wrap"
        gap={3}
        position="relative"
      >
        <HStack spacing={3}>
          <Box
            w="48px"
            h="48px"
            borderRadius="2xl"
            bgGradient="linear(135deg, #7c3aed, #22d3ee)"
            display="flex"
            alignItems="center"
            justifyContent="center"
            boxShadow="0 8px 20px -4px rgba(124,58,237,0.40)"
            position="relative"
          >
            <Icon as={LuChartColumn} color="white" boxSize="22px" />
            <Box
              position="absolute"
              inset="0"
              borderRadius="2xl"
              bgGradient="linear(135deg, rgba(255,255,255,0.25), transparent 50%)"
              pointerEvents="none"
            />
          </Box>
          <Box>
            <Heading size="md" color="gray.800" letterSpacing="-0.01em">{title ?? t('home.activityDashboard.title')}</Heading>
            <Text fontSize="xs" color="gray.500" mt={0.5}>
              {t(PERIOD_LABELS[period])} · {moduleFilterLabel(moduleFilter, t)}
            </Text>
          </Box>
        </HStack>

        <HStack spacing={2} flexWrap="wrap">
          {/* Compter des cas ou des bandelettes : deux lectures de la même
              activité, la même bascule que l'activité par établissement de
              l'admin. Cachée tant que le serveur ne ventile pas les
              bandelettes. */}
          {stripsAvailable && (
            <ButtonGroup size="sm" isAttached variant="outline">
              {([['cases', 'Cas'], ['strips', 'Bandelettes']] as Array<[Measure, string]>).map(([m, label]) => (
                <Button
                  key={m}
                  onClick={() => setMeasure(m)}
                  bg={m === effectiveMeasure ? 'teal.50' : undefined}
                  borderColor={m === effectiveMeasure ? 'teal.300' : undefined}
                  color={m === effectiveMeasure ? 'teal.700' : 'gray.600'}
                  fontWeight={m === effectiveMeasure ? '600' : '400'}
                  zIndex={m === effectiveMeasure ? 1 : 0}
                >
                  {label}
                </Button>
              ))}
            </ButtonGroup>
          )}
          {/* Period filter */}
          <Menu>
            <MenuButton
              as={Button}
              size="sm"
              variant="outline"
              rightIcon={<LuChevronDown />}
              fontWeight="medium"
            >
              {t(PERIOD_LABELS[period])}
            </MenuButton>
            <MenuList fontSize="sm">
              {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
                <MenuItem key={p} onClick={() => setPeriod(p)} fontWeight={period === p ? 'bold' : 'normal'}>
                  {t(PERIOD_LABELS[p])}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>

          {/* Module filter */}
          <Menu>
            <MenuButton
              as={Button}
              size="sm"
              variant="outline"
              rightIcon={<LuChevronDown />}
              fontWeight="medium"
            >
              {moduleFilterLabel(moduleFilter, t)}
            </MenuButton>
            <MenuList fontSize="sm">
              {(['all', ...visibleApps.map((a) => a.id)] as ModuleFilter[]).map((m) => (
                <MenuItem key={m} onClick={() => setModuleFilter(m)} fontWeight={moduleFilter === m ? 'bold' : 'normal'}>
                  {moduleFilterLabel(m, t)}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </HStack>
      </Flex>

      {error && (
        <Box bg="red.50" borderRadius="lg" p={4} mb={4}>
          <Text fontSize="sm" color="red.700">
            Impossible de charger les statistiques.
          </Text>
        </Box>
      )}

      {/* KPI cards */}
      {/* Une carte par application visible, servie par le registre : chaque
          application garde l'icône de sa carte du Lab Studio. */}
      <SimpleGrid columns={{ base: 2, md: Math.min(shownApps.length + 2, 5) }} spacing={3} mb={6}>
        <KpiCard
          icon={LuTestTube}
          label={effectiveMeasure === 'strips' ? 'Bandelettes totales' : t('home.activityDashboard.totalTests')}
          value={isLoading ? null : totalsHeader}
          accent="cyan"
        />
        {shownApps.map((app) => (
          <KpiCard
            key={app.id}
            icon={app.icon as React.ElementType}
            label={app.label}
            value={isLoading ? null : appCount(app)}
            accent={app.colorScheme}
          />
        ))}
        <KpiCard
          icon={LuTrendingUp}
          label={t('home.activityDashboard.mostTested')}
          value={null}
          customText={isLoading ? null : mostUsedPathology}
          accent="orange"
        />
      </SimpleGrid>

      {/* Charts */}
      <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={5} position="relative">
        {/* Bar chart — monthly evolution */}
        <Box
          gridColumn={{ base: 'auto', lg: 'span 2' }}
          bg="linear-gradient(135deg, #f8fafc 0%, #f0fdfa 100%)"
          borderRadius="xl"
          p={5}
          border="1px solid"
          borderColor="gray.100"
          position="relative"
          overflow="hidden"
          transition="all 0.25s"
          _hover={{ boxShadow: '0 8px 24px -10px rgba(20,184,166,0.18)' }}
        >
          {/* Left accent bar */}
          <Box
            position="absolute"
            top={5}
            bottom={5}
            left={0}
            w="3px"
            borderRadius="full"
            bgGradient="linear(to-b, teal.400, cyan.400)"
          />
          <HStack justify="space-between" mb={3} pl={2}>
            <HStack spacing={2}>
              <Text fontSize="sm" fontWeight="bold" color="gray.800">
                Évolution mensuelle
              </Text>
            </HStack>
            <Badge
              bgGradient="linear(135deg, teal.400, cyan.500)"
              color="white"
              fontSize="2xs"
              fontWeight="bold"
              borderRadius="full"
              px={2.5}
              py={0.5}
              boxShadow="0 2px 6px rgba(20,184,166,0.30)"
            >
              {totalsHeader} {unitWord}
            </Badge>
          </HStack>
          <Box h="220px">
            {isLoading ? (
              <Skeleton height="100%" borderRadius="md" />
            ) : barData && totalsHeader > 0 ? (
              <Bar
                data={barData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: { boxWidth: 10, boxHeight: 10, font: { size: 11 }, padding: 12 },
                    },
                    tooltip: { mode: 'index', intersect: false },
                  },
                  scales: {
                    x: { stacked: true, grid: { display: false }, ticks: { font: { size: 10 } } },
                    y: { stacked: true, beginAtZero: true, ticks: { precision: 0, font: { size: 10 } } },
                  },
                }}
              />
            ) : (
              <EmptyState message={t('home.activityDashboard.noTests')} />
            )}
          </Box>
        </Box>

        {/* Doughnut chart — by pathology */}
        <Box
          bg="linear-gradient(135deg, #f8fafc 0%, #faf5ff 100%)"
          borderRadius="xl"
          p={5}
          border="1px solid"
          borderColor="gray.100"
          position="relative"
          overflow="hidden"
          transition="all 0.25s"
          _hover={{ boxShadow: '0 8px 24px -10px rgba(168,85,247,0.18)' }}
        >
          {/* Left accent bar */}
          <Box
            position="absolute"
            top={5}
            bottom={5}
            left={0}
            w="3px"
            borderRadius="full"
            bgGradient="linear(to-b, purple.400, pink.400)"
          />
          <Text fontSize="sm" fontWeight="bold" color="gray.800" mb={3} pl={2}>
            Par pathologie
          </Text>
          <Box h="220px" position="relative">
            {isLoading ? (
              <Skeleton height="100%" borderRadius="md" />
            ) : doughnutData && totalsHeader > 0 ? (
              <>
                <Doughnut
                  data={doughnutData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: '72%',
                    plugins: {
                      // Legend disabled — the "Top pathologies" list right below
                      // already shows the breakdown with bars + counts, so a
                      // legend here would just duplicate information. Hiding it
                      // also lets us center the chart properly so the React
                      // overlay (animated total) sits exactly in the donut hole.
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: (ctx: any) => {
                            const total = ctx.dataset.data.reduce((a: number, b: number) => a + b, 0)
                            const pct = total > 0 ? Math.round((ctx.parsed / total) * 100) : 0
                            return ` ${ctx.label}: ${ctx.parsed} (${pct}%)`
                          },
                        },
                      },
                    },
                  }}
                />
                {/* Animated total overlay — driven by React state so the count-up
                    animation actually plays. Positioned at the geometric centre
                    of the chart container (legend is off, so 50%/50% works). */}
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                  textAlign="center"
                  pointerEvents="none"
                  zIndex={1}
                >
                  <Text fontSize="2xs" color="gray.500" letterSpacing="0.08em" fontWeight="bold">
                    TOTAL
                  </Text>
                  <Text
                    fontSize="3xl"
                    fontWeight="bold"
                    color="gray.800"
                    lineHeight="1"
                    letterSpacing="-0.02em"
                    mt={0.5}
                  >
                    {animatedTotal}
                  </Text>
                </Box>
              </>
            ) : (
              <EmptyState message={t('home.activityDashboard.noData')} />
            )}
          </Box>
        </Box>
      </SimpleGrid>

      {/* Top pathologies list */}
      {!isLoading && visiblePathologies.length ? (
        <Box mt={6} pt={5} borderTop="1px dashed" borderColor="gray.200">
          <HStack mb={3} justify="space-between">
            <HStack spacing={2}>
              <Box w="3px" h="14px" borderRadius="full" bgGradient="linear(to-b, teal.400, cyan.400)" />
              <Text fontSize="xs" color="gray.700" fontWeight="bold" letterSpacing="0.08em">
                TOP PATHOLOGIES
              </Text>
            </HStack>
            <Text fontSize="2xs" color="gray.400">
              {visiblePathologies.length} pathologie{visiblePathologies.length > 1 ? 's' : ''}
            </Text>
          </HStack>
          <VStack align="stretch" spacing={2}>
            {visiblePathologies.slice(0, 5).map((p, i) => {
              // Share of SEARCHES, not of cases: a Dynablot multitest case
              // carries several searches and is counted once for each, so the
              // number of cases is no longer the right denominator — with it
              // the shares would add up to more than 100%.
              const pct = pathologyTotal > 0 ? Math.round((pathoValue(p) / pathologyTotal) * 100) : 0
              const color = PALETTE[i % PALETTE.length]
              return (
                <Flex
                  key={p.name}
                  align="center"
                  gap={3}
                  py={1.5}
                  px={2}
                  borderRadius="md"
                  transition="all 0.15s"
                  _hover={{ bg: 'gray.50' }}
                >
                  {/* Rank pill */}
                  <Box
                    minW="22px"
                    h="22px"
                    px={1.5}
                    borderRadius="md"
                    bg={color}
                    color="white"
                    fontSize="2xs"
                    fontWeight="bold"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    boxShadow={`0 2px 6px ${color.replace('0.9)', '0.25)')}`}
                  >
                    {i + 1}
                  </Box>
                  <Text fontSize="sm" color="gray.800" flex={1} noOfLines={1} fontWeight="medium">
                    {pathologyDisplayName(p.name)}
                  </Text>
                  <Box flex={2} h="8px" bg="gray.100" borderRadius="full" overflow="hidden" position="relative">
                    <Box
                      h="100%"
                      w={`${pct}%`}
                      bg={color}
                      borderRadius="full"
                      transition="width 0.6s cubic-bezier(0.4, 0, 0.2, 1)"
                      position="relative"
                      _after={{
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        bgGradient: 'linear(to-r, rgba(255,255,255,0.30), transparent 60%)',
                        borderRadius: 'full',
                      }}
                    />
                  </Box>
                  <Box minW="64px" textAlign="right">
                    <Text fontSize="sm" color="gray.800" fontWeight="bold" lineHeight="1.1">
                      {pathoValue(p)}
                    </Text>
                    <Text fontSize="2xs" color="gray.500" lineHeight="1">
                      {pct}%
                    </Text>
                  </Box>
                </Flex>
              )
            })}
          </VStack>
        </Box>
      ) : null}
    </Box>
  )
}

function KpiCard({
  icon,
  label,
  value,
  customText,
  accent,
}: {
  icon: any
  label: string
  value: number | null
  customText?: string | null
  /** Un colorScheme Chakra — celui de l'application dans le registre. */
  accent: string
}) {
  const accentMap: Record<string, { bg: string; color: string; gradient: string; glow: string }> = {
    teal:   { bg: 'teal.50',   color: 'teal.600',   gradient: 'linear(135deg, #14b8a6, #0d9488)', glow: 'rgba(20,184,166,0.20)' },
    cyan:   { bg: 'cyan.50',   color: 'cyan.600',   gradient: 'linear(135deg, #06b6d4, #0891b2)', glow: 'rgba(6,182,212,0.20)' },
    purple: { bg: 'purple.50', color: 'purple.600', gradient: 'linear(135deg, #a855f7, #7c3aed)', glow: 'rgba(168,85,247,0.20)' },
    orange: { bg: 'orange.50', color: 'orange.600', gradient: 'linear(135deg, #f97316, #ea580c)', glow: 'rgba(249,115,22,0.20)' },
  }
  // Un colorScheme hors table (la prochaine application) garde des tons
  // corrects via les jetons Chakra, sans dégradé dédié.
  const a = accentMap[accent] ?? {
    bg: `${accent}.50`,
    color: `${accent}.600`,
    gradient: `linear(135deg, var(--chakra-colors-${accent}-400), var(--chakra-colors-${accent}-600))`,
    glow: 'rgba(100,116,139,0.20)',
  }
  return (
    <Box
      bg="white"
      borderRadius="xl"
      border="1px solid"
      borderColor="gray.100"
      p={4}
      pt={3.5}
      position="relative"
      overflow="hidden"
      transition="all 0.25s cubic-bezier(0.4, 0, 0.2, 1)"
      _hover={{
        transform: 'translateY(-2px)',
        boxShadow: `0 10px 20px -8px ${a.glow}`,
        borderColor: a.color,
      }}
    >
      {/* Accent strip at top */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        h="2px"
        bgGradient={a.gradient}
      />
      <HStack spacing={3} align="center">
        <Box
          w={10}
          h={10}
          borderRadius="xl"
          bgGradient={a.gradient}
          display="flex"
          alignItems="center"
          justifyContent="center"
          boxShadow={`0 4px 10px ${a.glow}`}
          position="relative"
        >
          <Icon as={icon} color="white" boxSize={4} />
          <Box
            position="absolute"
            inset="0"
            borderRadius="xl"
            bgGradient="linear(135deg, rgba(255,255,255,0.25), transparent 55%)"
            pointerEvents="none"
          />
        </Box>
        <VStack align="start" spacing={0.5} flex={1} minW={0}>
          <Text fontSize="2xs" color="gray.500" letterSpacing="0.07em" fontWeight="bold">
            {label.toUpperCase()}
          </Text>
          {value !== null ? (
            <Text fontSize="2xl" fontWeight="bold" color="gray.800" lineHeight="1" letterSpacing="-0.02em">
              {value}
            </Text>
          ) : customText !== undefined && customText !== null ? (
            <Text fontSize="sm" fontWeight="bold" color="gray.800" noOfLines={1}>
              {customText}
            </Text>
          ) : (
            <SkeletonText noOfLines={1} skeletonHeight={4} width="60px" />
          )}
        </VStack>
      </HStack>
    </Box>
  )
}

function EmptyState({ message }: { message: string }) {
  return (
    <Flex h="100%" align="center" justify="center" direction="column" color="gray.400">
      <Box
        w={14}
        h={14}
        borderRadius="full"
        bg="gray.100"
        display="flex"
        alignItems="center"
        justifyContent="center"
        mb={3}
      >
        <Icon as={LuActivity} boxSize={6} />
      </Box>
      <Text fontSize="sm" fontWeight="medium">{message}</Text>
    </Flex>
  )
}
